import { RefreshCw } from "lucide-react";
import { LoadingText } from "../ui";
import { useOverviewData, useStatusTabs } from "../../hooks";
import KpiCard from "./KpiCard";
import NarrativeCard from "./NarrativeCard";
import PlatformStatusCards from "./PlatformStatusCards";
import OverviewTab from "./OverviewTab";
import IncidentsTab from "./IncidentsTab";
import AlarmsTab from "./AlarmsTab";
import { timeAgo, toDatetimeLocal } from "./statusFormat";
import type { GraphSource, KpiMetric, LayerSource, Platform, RangePreset } from "../../types/status";

export default function PlatformDashboard({
  platform,
  isAdmin,
}: {
  platform?: Platform;
  isAdmin: boolean;
}) {
  const availableTabs = ["overview", "incidents", "alarms"];
  const { activeTab, setActiveTab, drillFilter, drillTo, clearDrill } = useStatusTabs(availableTabs);
  const ov = useOverviewData(platform);
  const kpiData = ov.platformKpis ?? ov.frontendKpis;

  function kpiDates() {
    if (!kpiData) return null;
    return { from: toDatetimeLocal(kpiData.from_dt), to: toDatetimeLocal(kpiData.to_dt) };
  }

  function handleUptimeClick() {
    const dates = kpiDates();
    if (!dates) return;
    drillTo({ ...dates, severity: "critical", label: "Critical incidents — driving uptime metric" });
  }

  function handleMttrClick() {
    const dates = kpiDates();
    if (!dates) return;
    drillTo({ ...dates, status: "resolved", label: "Resolved incidents — recovery time this period" });
  }

  function handleNarrativeDrillDown() {
    if (!ov.narrative?.incident_ids.length) return;
    drillTo({ ids: ov.narrative.incident_ids, label: "Incidents from health summary" });
  }

  function handleDowntimePeriodClick(incidentIds: string[]) {
    drillTo({ ids: incidentIds, label: "Incidents from downtime period" });
  }

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "incidents", label: "Incidents" },
    { id: "alarms", label: "Alarms" },
  ];

  if (ov.loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <LoadingText>Loading…</LoadingText>
      </div>
    );
  }

  const overviewDays = ov.range === "custom" ? 7 : Math.min(parseInt(ov.range), 30);
  const lastChecked = ov.summary?.integrations?.[0]?.last_checked_at;

  return (
    <div>
      <div className="mb-6 flex gap-1 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <>
          <p className="mb-5 text-sm text-gray-500">
            {platform ? `${PLATFORM_LABEL[platform]} view` : "Combined view · all platforms"}
            {lastChecked && <> · updated {timeAgo(lastChecked)}</>}
          </p>

          <NarrativeCard
            narrative={ov.narrative}
            loading={ov.narrativeLoading}
            onDrillDown={handleNarrativeDrillDown}
          />

          {!platform && <PlatformStatusCards days={overviewDays} />}

          <div className="mb-8">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {(["7", "30", "90", "custom"] as RangePreset[]).map((p) => (
                <button
                  key={p}
                  onClick={() => ov.setRange(p)}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                    ov.range === p
                      ? "border-blue-400 bg-blue-50 text-blue-700"
                      : "border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700"
                  }`}
                >
                  {p === "custom" ? "Custom" : `${p}d`}
                </button>
              ))}
              {ov.range === "custom" && (
                <>
                  <input
                    type="datetime-local"
                    value={ov.customFrom}
                    onChange={(e) => ov.setCustomFrom(e.target.value)}
                    className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
                  />
                  <span className="text-xs text-gray-400">to</span>
                  <input
                    type="datetime-local"
                    value={ov.customTo}
                    onChange={(e) => ov.setCustomTo(e.target.value)}
                    className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
                  />
                </>
              )}
              {ov.kpiLoading && <RefreshCw size={12} className="animate-spin text-gray-400" />}
              <button
                onClick={() => ov.reload(true)}
                disabled={ov.refreshing}
                className="ml-auto flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-gray-300 hover:text-gray-800 disabled:opacity-50"
              >
                <RefreshCw size={12} className={ov.refreshing ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              {KPI_GROUPS.map((group) => {
                const kpis = group.source === "platform" ? ov.platformKpis : ov.frontendKpis;
                return (
                  <div key={group.source}>
                    <div className="mb-2">
                      <h3 className="text-sm font-semibold text-gray-800">{group.title}</h3>
                      <p className="text-xs text-gray-400">{group.hint}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <KpiCard label="Uptime" value={kpis?.uptime.value ?? null} unit="%" metric={kpis?.uptime ?? EMPTY_METRIC} higherIsBetter onClick={handleUptimeClick} />
                      <KpiCard label="Average Time to Fix" value={kpis?.mttr.value ?? null} unit=" min" metric={kpis?.mttr ?? EMPTY_METRIC} higherIsBetter={false} onClick={handleMttrClick} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-3 flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500">Uptime history:</span>
            {GRAPH_SOURCES.map((opt) => (
              <button
                key={opt.value}
                onClick={() => ov.setGraphSource(opt.value)}
                className={`rounded-lg border px-3 py-1 text-xs font-medium transition-colors ${
                  ov.graphSource === opt.value
                    ? "border-blue-400 bg-blue-50 text-blue-700"
                    : "border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <OverviewTab
            data={ov.summary}
            uptimeGraph={ov.uptimeGraph}
            uptimeLoading={ov.uptimeLoading}
            onDowntimePeriodClick={handleDowntimePeriodClick}
            sourceLabel={GRAPH_SOURCE_LABEL[ov.graphSource]}
          />

          <p className="mt-6 text-center text-xs text-gray-400">Checks run every 5 minutes · Auto-refreshes every 60 seconds</p>
        </>
      )}
      {activeTab === "incidents" && (
        <IncidentsTab
          isAdmin={isAdmin}
          drillFilter={drillFilter}
          onClearDrill={clearDrill}
          platform={platform}
        />
      )}
      {activeTab === "alarms" && <AlarmsTab platform={platform} />}
    </div>
  );
}

const PLATFORM_LABEL: Record<string, string> = { phoenix: "Phoenix", kraken: "Kraken" };

const EMPTY_METRIC: KpiMetric = { value: null, prev_value: null, change_pct: null };

const KPI_GROUPS: { source: LayerSource; title: string; hint: string }[] = [
  { source: "platform", title: "Platform", hint: "From infrastructure & signoz signals" },
  { source: "frontend", title: "Front-end", hint: "From monitored URL checks" },
];

const GRAPH_SOURCES: { value: GraphSource; label: string }[] = [
  { value: "all", label: "All" },
  { value: "platform", label: "Platform" },
  { value: "frontend", label: "Front-end" },
];

const GRAPH_SOURCE_LABEL: Record<GraphSource, string> = {
  all: "all sources",
  platform: "Platform",
  frontend: "Front-end",
};
